/**
 * TanStack Query Provider (Day-15 Module 11 - Performance Optimization)
 * 
 * Cache rules:
 * - staleTime: 2 minutes (data stays fresh for 2 minutes)
 * - cacheTime: 5 minutes (data stays in cache for 5 minutes after becoming unused)
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Cache timing constants (in milliseconds)
const STALE_TIME = 2 * 60 * 1000; // 2 minutes
const GC_TIME = 5 * 60 * 1000; // 5 minutes (garbage collection time, formerly cacheTime)

// Create QueryClient with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * QueryProvider component - wraps app with TanStack Query context
 */
export function QueryProvider({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

// Export queryClient for manual cache operations
export { queryClient, STALE_TIME, GC_TIME };

// Query key factories for consistent cache key generation
export const queryKeys = {
  // Shifts
  shifts: {
    all: ['shifts'],
    myShifts: (filters) => ['shifts', 'my', filters],
    detail: (id) => ['shifts', 'detail', id],
    roster: (filters) => ['shifts', 'roster', filters],
  },
  
  // Participants
  participants: {
    all: ['participants'],
    list: (filters) => ['participants', 'list', filters],
    detail: (id) => ['participants', 'detail', id],
    profile: (id) => ['participants', 'profile', id],
    carePlan: (id) => ['participants', 'carePlan', id],
    funding: (id) => ['participants', 'funding', id],
  },
  
  // Medications
  medications: {
    all: ['medications'],
    chart: (participantId, filters) => ['medications', 'chart', participantId, filters],
    schedule: (participantId) => ['medications', 'schedule', participantId],
    history: (participantId, filters) => ['medications', 'history', participantId, filters],
  },
  
  // Houses
  houses: {
    all: ['houses'],
    list: (filters) => ['houses', 'list', filters],
    detail: (id) => ['houses', 'detail', id],
    logs: (houseId, filters) => ['houses', 'logs', houseId, filters],
    rooms: (houseId) => ['houses', 'rooms', houseId],
  },
  
  // Staff
  staff: {
    all: ['staff'],
    list: (filters) => ['staff', 'list', filters],
    detail: (id) => ['staff', 'detail', id],
    compliance: (id) => ['staff', 'compliance', id],
  },
  
  // Notifications
  notifications: {
    all: ['notifications'],
    list: (filters) => ['notifications', 'list', filters],
    counts: () => ['notifications', 'counts'],
  },
  
  // Dashboard
  dashboard: {
    main: ['dashboard', 'main'],
    mobile: ['dashboard', 'mobile'],
    stats: (type) => ['dashboard', 'stats', type],
  },
};

export default QueryProvider;
